import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Sofa } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Thumb } from '../../../../components/design-system/analytics';
import { formatDateTime } from '../../../../components/design-system/admin';
import type { LeadFile } from '../../../../common/hooks/use-lead-desk-api.hook';

/** What the shopper said to the shop's assistant: their words, its answers, the products shown, the photo they sent. */
export function LeadConversation({ threads, onImage }: { threads: LeadFile['threads']; onImage?: (url: string) => void }) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col gap-6">
            {threads.map((thread) => (
                <section key={thread.uuid} className="flex flex-col gap-3" aria-label={formatDateTime(thread.startedAt)}>
                    <p className="text-xs text-muted-foreground">{formatDateTime(thread.startedAt)}</p>
                    <ol className="flex flex-col gap-3">
                        {thread.messages.map((m, i) => {
                            const shopper = m.role === 'visitor';
                            return (
                                <li key={i} className={cn('flex flex-col gap-1.5', shopper ? 'items-start' : 'items-end')}>
                                    <span className="text-[11px] text-muted-foreground">
                                        {shopper ? t('LEAD_DESK.FILE.SHOPPER') : t('LEAD_DESK.FILE.ASSISTANT')}
                                        <span className="ms-1.5 tabular-nums">{dayjs(m.at).format('HH:mm')}</span>
                                    </span>
                                    {m.photo ? (
                                        m.photo.imageUrl ? (
                                            <button type="button" onClick={() => onImage?.(m.photo!.imageUrl!)} className="overflow-hidden rounded-xl border">
                                                <img src={m.photo.imageUrl} alt={t('LEAD_DESK.FILE.PHOTO_SENT')} className="h-32 w-auto object-cover" />
                                            </button>
                                        ) : (
                                            // The photo itself stays the shopper's: the chat's notice says the shop never sees it.
                                            <span className="rounded-xl border border-dashed px-3 py-2 text-xs text-muted-foreground" data-testid="lead-chat-photo-private">{t('LEAD_DESK.FILE.PHOTO_PRIVATE')}</span>
                                        )
                                    ) : m.render && !m.text ? (
                                        <span className="inline-flex items-center gap-1.5 rounded-xl border border-dashed px-3 py-2 text-xs text-muted-foreground">
                                            <Sofa className="size-3.5" aria-hidden />
                                            {t('LEAD_DESK.FILE.RENDER_SHOWN')}
                                        </span>
                                    ) : m.text ? (
                                        <div
                                            className={cn(
                                                'max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
                                                shopper ? 'rounded-ss-md bg-muted' : 'rounded-se-md border bg-background',
                                            )}
                                        >
                                            {m.text}
                                        </div>
                                    ) : null}
                                    {!shopper && m.products.length > 0 && (
                                        <div className="flex max-w-[85%] flex-wrap justify-end gap-2">
                                            {m.products.map((p) => (
                                                <span key={p.uuid} className="inline-flex max-w-[200px] items-center gap-2 rounded-lg border px-2 py-1.5 text-xs">
                                                    <Thumb src={p.image} alt={p.name} size="xs" />
                                                    <span className="truncate">{p.name}</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                </section>
            ))}
        </div>
    );
}
